/**
 * Morphing Gradient Background Animation
 * Część 2: Klasa NectarGradient
 */

// Główna klasa generująca gradienty
class NectarGradient {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.simplex = new window.SimplexNoise();
    this.setup();

    // Dodanie właściwości opacity dla samego gradientu
    this.gradientOpacity = 0;
    this.targetOpacity =
      parseFloat(this.canvas.getAttribute("data-opacity")) || 1;
    this.fadeInDuration =
      +this.canvas.getAttribute("data-fadein-duration") || 1500; // ms
    this.startTime = Date.now();

    this.rafLoop();

    // Dodajemy klasę loaded po zakończeniu setupu
    setTimeout(() => canvas.classList.add("loaded"), 100);
  }

  setup() {
    // Konfiguracja podstawowa
    this.onMobile = window.innerWidth < 690;
    this.clock = 0;
    this.resolution = this.onMobile ? 90 : 110;
    this.resPercentageNormalized = 1 / this.resolution;
    this.canvas.height = this.canvas.width = this.resolution;
    this.imgdata = this.ctx.getImageData(
      0,
      0,
      this.resolution,
      this.resolution
    );
    this.data = this.imgdata.data;
    [this.cx, this.cy] = [this.canvas.width / 2, this.canvas.height / 2];

    // Kolory i ustawienia
    const color1Raw = this.canvas.getAttribute("data-color1") || "#e3e3e3";
    const color2Raw = this.canvas.getAttribute("data-color2") || "#ffffff";

    this.settings = {
      speed: +this.canvas.getAttribute("data-speed") || 1000,
      color_1: this.getCSSVariableColor(color1Raw),
      color_2: this.getCSSVariableColor(color2Raw),
      blending_mode: this.canvas.getAttribute("data-blendingmode") || "linear",
    };

    this.colorSpeed = this.settings.speed === 1300 ? 0.5 : 0.9;
    this.colors = [this.settings.color_1, this.settings.color_2]
      .filter((c) => c)
      .map(this.rgbObj);
    this.singleColor = this.colors.length < 2;

    // Obsługa zmian rozmiaru
    this.resize();
    this.inView = true;
    window.addEventListener("resize", () => this.resize());
  }

  getCSSVariableColor(variableName) {
    if (variableName.startsWith("#")) return variableName;

    // Sprawdź czy string zawiera kolor hex gdziekolwiek (#ffffff lub #fff)
    const hexMatch = variableName.match(/#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})\b/);
    if (hexMatch) {
      return hexMatch[0];
    }

    // Sprawdź czy string zawiera var() gdziekolwiek
    const varMatch = variableName.match(/var\((.*?)\)/);
    if (varMatch) {
      const cssVarName = varMatch[1];
      return (
        getComputedStyle(document.documentElement)
          .getPropertyValue(cssVarName)
          .trim() || "#ffffff"
      );
    }

    if (variableName.startsWith("--")) {
      return (
        getComputedStyle(document.documentElement)
          .getPropertyValue(variableName)
          .trim() || "#ffffff"
      );
    }

    return variableName;
  }

  resize() {
    const [w, h] = [this.canvas.clientWidth, this.canvas.clientHeight];
    const ratio = h / w;
    this.aspectMod =
      ratio < 1 ? { x: 1.4, y: ratio * 1.4 } : { x: ratio / 3, y: 1 };
  }

  rgbObj(hex) {
    if (!hex || typeof hex !== "string") return { r: 255, g: 255, b: 255 };

    hex = hex.replace(/^#/, "");
    if (hex.length === 3)
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];

    const match = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return match
      ? {
          r: parseInt(match[1], 16),
          g: parseInt(match[2], 16),
          b: parseInt(match[3], 16),
        }
      : { r: 255, g: 255, b: 255 };
  }

  mix(v1, v2, a) {
    return v1 * (1 - a) + v2 * a;
  }

  rotate(x, y, angle) {
    const radians = (Math.PI / 180) * angle,
      cos = Math.cos(radians),
      sin = Math.sin(radians);
    return cos * (x - this.cx) + sin * (y - this.cy) + this.cx;
  }

  mixed(channel, x, y, noise) {
    if (this.singleColor) return this.colors[0][channel];

    const blendFactor =
      this.rotate(x, y, this.clock * this.colorSpeed) *
      this.resPercentageNormalized *
      (this.settings.blending_mode === "organic" ? 3.5 : 2);

    return this.mix(
      this.colors[0][channel],
      this.colors[1][channel],
      (blendFactor * noise) / 2
    );
  }

  // Funkcja do obliczania aktualnej przezroczystości gradientu
  updateGradientOpacity() {
    const elapsed = Date.now() - this.startTime;
    if (elapsed >= this.fadeInDuration) {
      this.gradientOpacity = this.targetOpacity;
      return true; // Animacja zakończona
    }
    // Płynna funkcja easeInOut
    const progress = elapsed / this.fadeInDuration;
    this.gradientOpacity =
      progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
    return false; // Animacja wciąż trwa
  }

  rafLoop() {
    if (this.inView) {
      // Aktualizacja opacity gradientu
      const animationComplete = this.updateGradientOpacity();

      for (let x = 0; x < this.resolution; x++) {
        for (let y = 0; y < this.resolution; y++) {
          const noise = this.simplex.noise3D(
            (x * this.aspectMod.x) / this.resolution,
            (y * this.aspectMod.y) / this.resolution,
            this.clock / this.settings.speed
          );

          const idx = (x + y * this.resolution) * 4;
          this.data[idx] = this.mixed("r", x, y, noise);
          this.data[idx + 1] = this.mixed("g", x, y, noise);
          this.data[idx + 2] = this.mixed("b", x, y, noise);

          // Zastosowanie animacji opacity tylko do kanału alfa (przezroczystości)
          // Przemnażamy wartość szumu przez docelową przezroczystość (265) i aktualny postęp animacji
          this.data[idx + 3] = noise * 265 * this.gradientOpacity;
        }
      }
      this.clock++;
      this.ctx.putImageData(this.imgdata, 0, 0);
    }
    requestAnimationFrame(() => this.rafLoop());
  }
}

// Pomocnicza funkcja do konwersji kolorów (kopią logiki z klasy)
function getCSSVariableColor(variableName) {
  if (variableName.startsWith("#")) return variableName;

  // Sprawdź czy string zawiera kolor hex gdziekolwiek (#ffffff lub #fff)
  const hexMatch = variableName.match(/#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})\b/);
  if (hexMatch) {
    return hexMatch[0];
  }

  // Sprawdź czy string zawiera var() gdziekolwiek
  const varMatch = variableName.match(/var\((.*?)\)/);
  if (varMatch) {
    const cssVarName = varMatch[1];
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue(cssVarName)
        .trim() || "#ffffff"
    );
  }

  if (variableName.startsWith("--")) {
    return (
      getComputedStyle(document.documentElement)
        .getPropertyValue(variableName)
        .trim() || "#ffffff"
    );
  }

  return variableName;
}

// Funkcja inicjalizująca wszystkie morphing gradienty na stronie
function initMorphingGradients() {
  // Znajdujemy wszystkie elementy z włączonym morphing gradient
  const elements = document.querySelectorAll('[data-morphing-gradient="true"]');

  elements.forEach((element, index) => {
    // Tworzymy kontener gradientu
    const gradientContainer = document.createElement("div");
    gradientContainer.className = "morphing-gradient-container";

    // Tworzymy canvas
    const canvas = document.createElement("canvas");
    canvas.setAttribute(
      "data-color1",
      getCSSVariableColor(
        element.getAttribute("data-gradient-color1") || "#e3e3e3"
      )
    );
    canvas.setAttribute(
      "data-color2",
      getCSSVariableColor(
        element.getAttribute("data-gradient-color2") || "#ffffff"
      )
    );
    canvas.setAttribute(
      "data-speed",
      element.getAttribute("data-gradient-speed") || "1000"
    );
    canvas.setAttribute(
      "data-blendingmode",
      element.getAttribute("data-gradient-blending") || "organic"
    );
    canvas.setAttribute(
      "data-fadein-duration",
      element.getAttribute("data-gradient-fadein") || "1500"
    );
    canvas.setAttribute(
      "data-opacity",
      element.getAttribute("data-gradient-opacity") || "1"
    );

    // Dodajemy canvas do kontenera
    gradientContainer.appendChild(canvas);

    // Obsługa nakładek gradientu
    const overlayTop =
      element.getAttribute("data-gradient-overlay-top") === "true";
    const overlayBottom =
      element.getAttribute("data-gradient-overlay-bottom") === "true";

    if (overlayTop || overlayBottom) {
      const overlayColorRaw =
        element.getAttribute("data-gradient-overlay-color") || "#ffffff";
      const overlaySize =
        element.getAttribute("data-gradient-overlay-size") || "30%";

      // Ustaw atrybuty na elemencie, które będą używane przez CSS
      if (overlayTop) {
        element.setAttribute("data-morphing-gradient-overlay-top", "true");
      }
      if (overlayBottom) {
        element.setAttribute("data-morphing-gradient-overlay-bottom", "true");
      }

      // Pobierz tylko hex (do pierwszej spacji) z overlayColor
      const overlayColor = overlayColorRaw.split(" ")[0];

      // Ustaw kolor overlay jako hex
      element.style.setProperty(
        "--morphing-gradient-overlay-color",
        overlayColor
      );

      // Ustaw rozmiar
      const sizeValue = overlaySize.toString();
      const hasUnit = /^[\d.]+[a-z%]+$/i.test(sizeValue);
      const finalSize = hasUnit ? sizeValue : `${sizeValue}px`;
      element.style.setProperty("--morphing-gradient-overlay-size", finalSize);
    }

    // Dodajemy kontener na początku elementu
    element.insertBefore(gradientContainer, element.firstChild);

    // Inicjalizujemy animację
    if (window.SimplexNoise) {
      new NectarGradient(canvas);
    }
  });
}

// Uruchamiamy inicjalizację po załadowaniu DOM
document.addEventListener("DOMContentLoaded", () => {
  // Sprawdzamy czy nie jesteśmy w edytorze Bricks
  if (!window.bricksIsEditor) {
    initMorphingGradients();
  }
});

// Eksport do globalnego zasięgu
window.NectarGradient = NectarGradient;
window.initMorphingGradients = initMorphingGradients;
